import { useNavigate } from 'react-router-dom';

export default function ConnectedTV() {
  const navigate = useNavigate();

  const features = [
    {
      icon: "📺",
      title: "Premium Inventory",
      description: "Access top-tier streaming platforms and premium CTV inventory."
    },
    {
      icon: "🎯",
      title: "Precision Targeting",
      description: "Reach specific households with advanced audience targeting."
    },
    {
      icon: "🤖",
      title: "AI Optimization",
      description: "Machine learning optimizes ad delivery for maximum impact."
    },
    {
      icon: "📊",
      title: "Real-Time Analytics",
      description: "Track performance across all streaming services in real-time."
    },
    {
      icon: "🔄",
      title: "Dynamic Creative",
      description: "Deliver personalized ads based on viewer preferences."
    },
    {
      icon: "💰",
      title: "Performance Tracking",
      description: "Measure brand lift, conversions, and ROAS across CTV campaigns."
    }
  ];

  const platforms = [
    "Roku", "Amazon Fire TV", "Apple TV", "Samsung TV Plus",
    "Hulu", "Peacock", "Paramount+", "HBO Max", "Disney+",
    "Netflix Ads", "YouTube TV", "Pluto TV"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00809D]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Connected TV
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Deliver AI-optimized CTV ads across 200+ streaming services.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
                Get Started
              </button>
              <button className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="bg-gradient-to-br from-[#00809D]/20 to-transparent backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">200+</div>
              <div className="text-gray-400">Streaming Services</div>
            </div>
            <div className="bg-gradient-to-br from-[#00809D]/20 to-transparent backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">120M</div>
              <div className="text-gray-400">Households Reached</div>
            </div>
            <div className="bg-gradient-to-br from-[#00809D]/20 to-transparent backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">95%</div>
              <div className="text-gray-400">Completion Rate</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            CTV Advertising Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gradient-to-b from-[#00809D]/10 to-transparent">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Streaming Platforms
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {platforms.map((platform, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all"
              >
                {platform}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#00809D]/20 to-[#00a0c4]/20 backdrop-blur-lg border border-white/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Reach Viewers on the Big Screen
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Advertise on premium streaming services where audiences are most engaged.
          </p>
          <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-10 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
            Launch CTV Campaign
          </button>
        </div>
      </section>
    </div>
  );
}
